import createSelector from 'lib/web/createSelector'
import memoize from 'lib/memoize/memoize'
import { arrayToObject, groupBy } from 'lib/array/arrayUtils'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import moment from 'moment'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import { getExtraGuestSurchargeTotal } from 'selectors/cart/cartAmountSelectors'
import { getPackageBasePriceTotal, getUpgradePackages } from './cartSelectors'

const emptyCalendar: App.Calendar = { packageId: undefined, months: [], flightCacheActive: false }

/** @deprecated */
export const getIsCalendarLoading = createSelector(
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.calendar.calendarsLoading,
  (pkg, calendars) => {
    if (pkg) {
      return calendars[pkg.uniqueKey] || false
    }
    return false
  },
)

/** @deprecated */
export const getPackageCalendar = createSelector(
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (pkg, calendars): App.Calendar => {
    return calendars[pkg?.uniqueKey] || emptyCalendar
  },
)

/** @deprecated */
export const getCalenderDaysByCheckIn = memoize((data: App.Calendar) => arrayToObject(
  data.months.flatMap(m => m.days),
  date => date.checkIn,
))

/** @deprecated */
export function getAvailableDayFromCalendar(
  checkIn: string,
  calenderData: App.Calendar,
  withFlights?: boolean,
) {
  const newDay = getCalenderDaysByCheckIn(calenderData)[checkIn]
  if (newDay) {
    const useFlights = withFlights && calenderData.flightCacheActive
    if (newDay.hotelAvailable && (!useFlights || newDay.flightsAvailable)) {
      return newDay
    }
  }
}

/** @deprecated */
export const getSelectedDay = createSelector(
  (state: App.State) => getPackageCalendar(state),
  (state: App.State) => state.cart.accommodation.startDate,
  (state: App.State) => state.router.location.search,
  (calendar, startDate, search) => {
    if (calendar) {
      const params = parseSearchString(search)
      return getAvailableDayFromCalendar(startDate, calendar, params.withFlights === 'true')
    }
  },
)

/** @deprecated */
export const getMonthCalendarAvailability = memoize((calendars: { [packageId: string]: App.Calendar }) => {
  const groupedMonths = groupBy(Object.values(calendars).flatMap(c => c.months),
    month => `${month.month}-${month.year}`,
  )

  return arrayToObject(
    Array.from(groupedMonths.entries()),
    ([key]) => key,
    (entry) => entry[1].some(m => m.days.some(d => d.hotelAvailable)),
  )
})

/** @deprecated */
const getMonthCalendarPrices = createSelector(
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => getPackageCalendar(state),
  (state: App.State) => getExtraGuestSurchargeTotal(state),
  (state: App.State) => getCartPackageBasePriceTotal(state),
  (state: App.State, withFlights: boolean) => withFlights,
  (
    offer,
    currentCalendar,
    extraGuestSurcharge,
    basePriceTotal,
    withFlights,
  ) => arrayToObject(currentCalendar.months,
    month => `${month.month}-${month.year}`,
    month => {
      let cheapestDay = month.cheapestDay
      if (withFlights && currentCalendar.flightCacheActive) {
        cheapestDay = month.cheapestDayWithFlights
      }
      if (!cheapestDay) {
        return Number.MAX_SAFE_INTEGER
      }

      let price = (offer.type === OFFER_TYPE_LAST_MINUTE || offer.type === OFFER_TYPE_ALWAYS_ON) ? cheapestDay.hotelPrice : basePriceTotal
      price += cheapestDay.surcharge
      price += extraGuestSurcharge || cheapestDay.extraGuestSurcharge || 0
      if (withFlights) {
        price += cheapestDay.flightPrice
      }
      return price
    }),
)

/** @deprecated */
export const getMonthCalendarViews = createSelector(
  (state: App.State) => getPackageCalendar(state),
  (state: App.State) => getMonthCalendarAvailability(state.calendar.calendarsByPackageKey),
  (state: App.State, withFlights: boolean) => getMonthCalendarPrices(state, withFlights),
  (state: App.State, withFlights: boolean) => withFlights,
  (
    currentCalendar,
    calendarAvailaiblity,
    pricesPerMonth,
    withFlights,
  ) => {
    const cheapestPrice = Math.min(...Object.values(pricesPerMonth))

    return currentCalendar.months.map(month => {
      const monthKey = `${month.month}-${month.year}`
      const price = pricesPerMonth[monthKey]
      const soldOut = !month.hotelAvailable
      const date = moment().utc().month(month.month).year(parseInt(month.year, 10)).startOf('month')
      const available = month.hotelAvailable && (!withFlights || month.flightsAvailable)

      return {
        originalMonth: month,
        date,
        available,
        soldOut,
        price,
        // Whether this month is the lowest for for the *entire calendar*
        // Lowest prices only shown for flights, business rule?
        isLowestPrice: !soldOut && currentCalendar.flightCacheActive && price === cheapestPrice,
        // Whether another calendar has avaiablity for this date
        hasOtherAvailability: calendarAvailaiblity[monthKey] ?? false,
      }
    })
  },
)

export type MonthCalendarView = ReturnType<typeof getMonthCalendarViews>[0]

/** @deprecated */
export const getDayCalendarAvailability = memoize((calendars: { [packageId: string]: App.Calendar }) => {
  const allMonths = Object.values(calendars).flatMap(c => c.months)
  const monthGroups = groupBy(allMonths, month => `${month.month}-${month.year}`)

  return Array.from(monthGroups.values()).map(months => {
    const allDaysForMonth = months.flatMap(m => m.days)
    return groupBy(allDaysForMonth, day => day.checkIn)
  }).reduce((dayByKey, dayMap) => {
    Array.from(dayMap.entries()).forEach(([key, days]) => {
      dayByKey[key] = days.some(d => !!d.hotelAvailable)
    })
    return dayByKey
  }, {})
},
)

/** @deprecated */
export const getDayCalendarUpgradeAvailability = createSelector(
  (state: App.State) => getUpgradePackages(state),
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (state: App.State) => state.cart.accommodation.originalPackageKey,
  (upgradeOptions, calendars, originalPackageKey) => {
    const upgradableUniqueKeys = upgradeOptions.map(o => o?.package?.uniqueKey).filter(key => key !== originalPackageKey)
    const upgradeCalendars = Object.fromEntries(Object.entries(calendars).filter(([key]) => upgradableUniqueKeys.includes(key)))
    return getDayCalendarAvailability(upgradeCalendars)
  },
)

/** @deprecated */
export const getCartPackageBasePriceTotal = createSelector(
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.cart.items.length,
  (state: App.State) => state.cart.bookDatesOnly,
  (currentPackage,
    packageCount,
    bookDatesOnly,
  ): number => {
    if (bookDatesOnly) {
      // we've already paid for the package, we're just selecting dates now
      return 0
    }

    const prices = getPackageBasePriceTotal(
      currentPackage.price,
      currentPackage.memberPrice,
      packageCount,
    )

    return prices.price
  },
)

/** @deprecated */
export const cartCalendarFlightsToggled = createSelector(
  (state: App.State) => state.router.location.search,
  (state: App.State) => state.cart.accommodation.offer,
  (search, offer) => offer?.offerFlightsEnabled && parseSearchString(search).withFlights === 'true',
)

/**
 *  This selector builds the *raw* data that'll be used for the day calendar views
 *  The set of data created from this is fairly static and will rarely change once initially
 *  calculated
 **/
/** @deprecated */
const getDayCalendarData = createSelector(
  (state: App.State) => getPackageCalendar(state),
  (state: App.State) => getExtraGuestSurchargeTotal(state),
  (state: App.State) => getDayCalendarAvailability(state.calendar.calendarsByPackageKey),
  (state: App.State) => getCartPackageBasePriceTotal(state),
  (state: App.State) => cartCalendarFlightsToggled(state),
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.checkout?.cart?.existingOrder?.items,
  (state: App.State) => state.cart.items[0]?.orderItemId,
  (state: App.State) => getDayCalendarUpgradeAvailability(state),
  (state: App.State) => getUpgradePackages(state),
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (state: App.State) => state.cart.accommodation.package,
  (calendar,
    extraGuestSurcharge,
    availability,
    basePriceTotal,
    withFlights,
    offer,
    bookedAccommodationItems,
    currentCartItemOrderId,
    upgradeAvailability,
    upgradeOptions,
    calendars,
    currentPackage,
  ) => calendar.months.map(month => {
    let cheapestDayPrice = Number.MAX_SAFE_INTEGER
    if (withFlights && calendar.flightCacheActive) {
      cheapestDayPrice = basePriceTotal + (month.cheapestDayWithFlights?.surcharge || 0) + (month.cheapestDayWithFlights?.flightPrice || 0)
    }

    let bookedAccommodationItem: App.OrderItem | undefined
    if (currentCartItemOrderId && bookedAccommodationItems) {
      bookedAccommodationItem = bookedAccommodationItems.find(item => item.id === currentCartItemOrderId)
    }
    const currentCheckIn = bookedAccommodationItem?.reservation?.startDate

    const bookedAccomdationSurcharge = bookedAccommodationItem?.reservation?.surcharge ?? 0
    const bookedAccommodationTotal = bookedAccommodationItem?.total ?? 0

    return {
      ...month,
      days: month.days.map(day => {
        let price = (offer.type === OFFER_TYPE_LAST_MINUTE || offer.type === OFFER_TYPE_ALWAYS_ON) ? day.hotelPrice : basePriceTotal
        price += day.surcharge
        price += extraGuestSurcharge || day.extraGuestSurcharge || 0

        if (withFlights) {
          price += day.flightPrice
        }
        const available = day.hotelAvailable && (!withFlights || day.flightsAvailable)
        let upgradeAvailable

        if (!available && upgradeAvailability[day.checkIn]) {
          upgradeAvailable = isUpgradeAvailable(calendars, day, upgradeOptions, currentPackage, currentCheckIn)
        }

        let priceDiff = day.surcharge + extraGuestSurcharge - bookedAccomdationSurcharge
        let hasTactical = false

        if (offer.type === OFFER_TYPE_ALWAYS_ON) {
          priceDiff = price - bookedAccommodationTotal
          hasTactical = day.hasTactical
        }

        return {
          originalDay: day,
          available,
          soldOut: !day.hotelAvailable,
          hasOtherAvailablity: availability[day.checkIn],
          upgradeAvailable,
          price,
          selected: false,
          isCheckIn: false,
          isCheckOut: false,
          isLowestPrice: available && cheapestDayPrice === price,
          canSwitchToHotel: withFlights && day.hotelAvailable,
          bookedAccomdationSurcharge,
          bookedAccommodationTotal,
          priceDiff,
          hasTactical,
        }
      }),
    }
  }),
)

/** @deprecated */
export function isUpgradeAvailable(calendars, day, upgradeOptions, currentPackage, currentCheckIn) {
  return upgradeOptions.some(o => {
    const currentPackageDayData = calendars[currentPackage?.uniqueKey] && getAvailableDayFromCalendar(currentCheckIn, calendars[currentPackage?.uniqueKey], false)
    const calendarDay = calendars[o?.package?.uniqueKey] && getAvailableDayFromCalendar(day.checkIn, calendars[o?.package?.uniqueKey], false)

    if (calendarDay) {
      return calendarDay.hotelTotal >= currentPackageDayData?.hotelTotal && calendarDay.hotelAvailable
    }
  })
}

/**
 * This selector utilises the raw data calculated above and then adds on
 * whether the day is selected or not
 *
 * This is done in a two stage process to ensure the entire data set isn't being re-rebuilt
 * every time a new date is changed. This way, only the things that change actually change!

/** @deprecated */
export const getDayCalendarViews = createSelector(
  (state: App.State) => getDayCalendarData(state),
  (state: App.State) => state.cart.accommodation.startDate,
  (state: App.State) => state.cart.accommodation.endDate,
  (calendarMonths, checkInDate, checkOutDate) => {
    if (checkInDate && checkOutDate) {
      const checkIn = moment(checkInDate).valueOf()
      const checkOut = moment(checkOutDate).valueOf()

      return calendarMonths.map(monthView => {
        const monthMoment = moment().startOf('month').year(parseInt(monthView.year, 10)).month(monthView.month)
        const monthHasSelectedDates = monthMoment.isBetween(checkIn, checkOut, 'month', '[]')

        if (monthHasSelectedDates) {
          return {
            ...monthView,
            days: monthView.days.map(dayView => {
              const day = new Date(monthMoment.year(), monthMoment.month(), dayView.originalDay.day).getTime()
              const selected = day >= checkIn && day <= checkOut

              if (selected) {
                return {
                  ...dayView,
                  selected,
                  isCheckIn: checkInDate === dayView.originalDay.checkIn,
                  isCheckOut: checkOutDate === dayView.originalDay.checkIn,
                }
              }
              return dayView
            }),
          }
        }
        return monthView
      })
    }

    return calendarMonths
  },
)

export type DayCalendarMonthView = ReturnType<typeof getDayCalendarViews>[0]
export type DayCalendarDayView = DayCalendarMonthView['days'][0]
